import {
  capMsToDailyLimit,
  endOfIstDayMs,
  getIstNowMs,
  isSameIstDay,
  MAX_IST_DAY_MS,
  parseApiDateTimeMs,
  startOfIstDayMs,
  startOfNextIstDayMs,
  toIstDateString,
} from './istTime';
import { buildLoginTimeMap, hmsToMs } from './loginTime';

function hasStopTime(value) {
  if (value == null || value === '') return false;
  const ms = parseApiDateTimeMs(value);
  if (Number.isNaN(ms)) return false;
  const d = new Date(ms + 5.5 * 60 * 60 * 1000);
  if (d.getUTCFullYear() < 1970) return false;
  return true;
}

function resolveTaskEndMs(startMs, stopRaw, referenceNowMs) {
  if (hasStopTime(stopRaw)) {
    const endMs = parseApiDateTimeMs(stopRaw);
    return Number.isNaN(endMs) || endMs <= startMs ? 0 : endMs;
  }
  return referenceNowMs;
}

/** Duration clipped to filter date range (per IST day). */
export function taskStartsInDateRange(item, startDate, endDate) {
  const startMs = parseApiDateTimeMs(item.startTime ?? item.StartTime);
  const itemDateStr = Number.isNaN(startMs) ? '' : toIstDateString(startMs);
  if (!itemDateStr || !startDate || !endDate) return false;
  return itemDateStr >= startDate && itemDateStr <= endDate;
}

/** Duration clipped to filter date range (per IST day). */
export function sessionDurationMsInRange(
  startRaw,
  stopRaw,
  filterStartDate,
  filterEndDate,
  referenceNow = Date.now()
) {
  const referenceNowMs = getIstNowMs(referenceNow);
  const startMs = parseApiDateTimeMs(startRaw);
  if (Number.isNaN(startMs)) return 0;

  const endMs = resolveTaskEndMs(startMs, stopRaw, referenceNowMs);
  if (!endMs || endMs <= startMs) return 0;

  if (!filterStartDate || !filterEndDate) {
    return Math.max(0, endMs - startMs);
  }

  let total = 0;
  let cursor = startMs;
  let guard = 0;

  while (cursor < endMs && guard < 366) {
    guard += 1;
    const dayStr = toIstDateString(cursor);
    if (!dayStr) break;

    if (dayStr < filterStartDate || dayStr > filterEndDate) {
      const nextSkip = startOfNextIstDayMs(cursor);
      if (Number.isNaN(nextSkip) || nextSkip <= cursor) break;
      cursor = nextSkip;
      continue;
    }

    const dayEnd = endOfIstDayMs(cursor);
    const segmentEnd = Math.min(endMs, dayEnd);
    if (segmentEnd > cursor) {
      total += segmentEnd - cursor;
    }

    const next = startOfNextIstDayMs(cursor);
    if (Number.isNaN(next) || next <= cursor) break;
    cursor = next;
  }

  return total;
}

export function sessionDurationMinutesInRange(
  startRaw,
  stopRaw,
  filterStartDate,
  filterEndDate,
  referenceNow = Date.now()
) {
  const diffMs = sessionDurationMsInRange(
    startRaw,
    stopRaw,
    filterStartDate,
    filterEndDate,
    referenceNow
  );
  if (diffMs <= 0) return 0;

  let mins = Math.floor(diffMs / 60000);
  if (mins === 0 && diffMs > 0) mins = 1;
  return mins;
}

export function taskDurationMinutesInRange(item, startDate, endDate, referenceNow = Date.now()) {
  return sessionDurationMinutesInRange(
    item.startTime ?? item.StartTime,
    item.stopTime ?? item.StopTime,
    startDate,
    endDate,
    referenceNow
  );
}

function clipToLoginWindows(startMs, endMs, userId, loginLogs, referenceNowMs) {
  if (!loginLogs?.length) return Math.max(0, endMs - startMs);

  let total = 0;
  (loginLogs || []).forEach((log) => {
    const logUserId = log.userId ?? log.UserId;
    if (Number(logUserId) !== Number(userId)) return;

    const loginMs = parseApiDateTimeMs(log.userLoginDate ?? log.UserLoginDate);
    if (Number.isNaN(loginMs)) return;

    const logoutRaw = log.userLogoutDate ?? log.UserLogoutDate;
    let logoutMs;
    if (hasStopTime(logoutRaw)) {
      logoutMs = parseApiDateTimeMs(logoutRaw);
    } else if (isSameIstDay(loginMs, referenceNowMs)) {
      logoutMs = referenceNowMs;
    } else {
      logoutMs = endOfIstDayMs(loginMs);
    }

    if (Number.isNaN(logoutMs) || logoutMs <= loginMs) return;

    const overlapStart = Math.max(startMs, loginMs);
    const overlapEnd = Math.min(endMs, logoutMs);
    if (overlapEnd > overlapStart) {
      total += overlapEnd - overlapStart;
    }
  });

  return total;
}

function distributeTaskSessionToMap(
  map,
  userId,
  startRaw,
  stopRaw,
  referenceNowMs,
  { loginLogs = null, clipToLogin = false, dateFilter = null } = {}
) {
  const startMs = parseApiDateTimeMs(startRaw);
  if (!userId || Number.isNaN(startMs)) return;

  const endMs = resolveTaskEndMs(startMs, stopRaw, referenceNowMs);
  if (!endMs || endMs <= startMs) return;

  let cursor = startMs;
  let guard = 0;
  while (cursor < endMs && guard < 366) {
    guard += 1;
    const dayStr = toIstDateString(cursor);
    if (!dayStr) break;

    if (dateFilter && (dayStr < dateFilter.fromDate || dayStr > dateFilter.toDate)) {
      const nextSkip = startOfNextIstDayMs(cursor);
      if (Number.isNaN(nextSkip) || nextSkip <= cursor) break;
      cursor = nextSkip;
      continue;
    }

    const dayEnd = endOfIstDayMs(cursor);
    const segmentEnd = endMs <= dayEnd ? endMs : dayEnd;
    let ms = segmentEnd - cursor;

    if (clipToLogin && loginLogs?.length) {
      ms = clipToLoginWindows(cursor, segmentEnd, userId, loginLogs, referenceNowMs);
    }

    if (ms > 0) {
      const key = `${userId}|${dayStr}`;
      map.set(key, (map.get(key) || 0) + ms);
    }

    const next = startOfNextIstDayMs(cursor);
    if (Number.isNaN(next) || next <= cursor) break;
    cursor = next;
  }
}

function sumTaskMsForUserInRange(
  records,
  userId,
  startDate,
  endDate,
  referenceNowMs,
  loginLogs = null
) {
  let totalMs = 0;
  const tempMap = new Map();

  (records || []).forEach((item) => {
    const itemUserId = item.userid ?? item.Userid ?? item.userId ?? item.UserId;
    if (Number(itemUserId) !== Number(userId)) return;

    distributeTaskSessionToMap(
      tempMap,
      userId,
      item.startTime ?? item.StartTime,
      item.stopTime ?? item.StopTime,
      referenceNowMs,
      {
        loginLogs,
        clipToLogin: Boolean(loginLogs?.length),
        dateFilter: { fromDate: startDate, toDate: endDate },
      }
    );
  });

  tempMap.forEach((ms) => {
    totalMs += ms;
  });

  return totalMs;
}

/** Card/checklist flag, or saved title suffix from desktop display. */
export function isCallMeetingTaskItem(item) {
  if (Boolean(item?.isCallMeeting ?? item?.IsCallMeeting)) return true;

  const cardTitle = (item?.cardTitle ?? item?.CardTitle ?? '').toLowerCase();
  const itemName = (item?.itemName ?? item?.ItemName ?? '').toLowerCase();
  const combined = `${cardTitle} ${itemName}`;
  return combined.includes('call/meeting') || combined.includes('call meeting');
}

export function sessionDurationMs(
  startRaw,
  stopRaw,
  referenceNow = Date.now(),
  options = {}
) {
  if (!startRaw) return 0;

  const referenceNowMs = getIstNowMs(referenceNow);
  const startMs = parseApiDateTimeMs(startRaw);
  if (Number.isNaN(startMs)) return 0;

  let endMs;
  if (!hasStopTime(stopRaw)) {
    if (!isSameIstDay(startMs, referenceNowMs)) return 0;
    endMs = referenceNowMs;
  } else {
    endMs = parseApiDateTimeMs(stopRaw);
    if (Number.isNaN(endMs)) return 0;
  }

  if (endMs <= startMs) return 0;

  const { userId = null, loginLogs = null, clipToLogin = false } = options;
  if (clipToLogin && loginLogs && userId != null) {
    return clipToLoginWindows(startMs, endMs, userId, loginLogs, referenceNowMs);
  }

  return endMs - startMs;
}

export function sessionDurationMinutes(startRaw, stopRaw, referenceNow = Date.now(), options = {}) {
  const diffMs = sessionDurationMs(startRaw, stopRaw, referenceNow, options);
  if (diffMs <= 0) return 0;

  let mins = Math.floor(diffMs / 60000);
  if (mins === 0 && diffMs > 0) mins = 1;
  return mins;
}

export function taskDurationMinutes(item, referenceNow = Date.now(), options = {}) {
  const startRaw = item.startTime ?? item.StartTime;
  const stopRaw = item.stopTime ?? item.StopTime;
  const userId = item.userid ?? item.Userid ?? item.userId ?? item.UserId;
  return sessionDurationMinutes(startRaw, stopRaw, referenceNow, {
    ...options,
    userId: options.userId ?? userId,
  });
}

export function getTaskLabelFromItem(item) {
  // Task Reports: only CardTitle (no ItemName).
  const base = (item.cardTitle || item.CardTitle || '').trim();
  if (isCallMeetingTaskItem(item) && base && !base.toLowerCase().includes('call/meeting')) {
    return `${base} - call/meeting`;
  }
  return base;
}

function matchesTaskFilters(item, { userName, taskName, startDate, endDate }) {
  const username = (item.userName || item.UserName || '').trim();
  const startMs = parseApiDateTimeMs(item.startTime ?? item.StartTime);
  const itemDateStr = Number.isNaN(startMs) ? '' : toIstDateString(startMs);
  if (!itemDateStr || !startDate || !endDate) return false;
  if (itemDateStr < startDate || itemDateStr > endDate) return false;

  if (userName && username.toLowerCase() !== userName.toLowerCase()) return false;

  if (taskName) {
    const label = getTaskLabelFromItem(item);
    if (label.toLowerCase() !== taskName.toLowerCase()) return false;
  }

  return true;
}

export function formatTaskMinutes(totalMinutes) {
  const mins = Number(totalMinutes);
  if (!mins || mins <= 0) return '0 min';
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  if (hours > 0) {
    return rem > 0 ? `${hours}h ${rem}min` : `${hours}h`;
  }
  return `${rem} min`;
}

/** Dashboard table format — matches existing HH:MM:SS style */
export function formatTaskMinutesAsHms(totalMinutes) {
  const total = Math.max(0, Number(totalMinutes) || 0);
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
}

export function buildTaskTimeMap(records, referenceNow = Date.now(), loginLogs = null) {
  const map = new Map();
  const referenceNowMs = getIstNowMs(referenceNow);

  (records || []).forEach((item) => {
    const userId = item.userid ?? item.Userid ?? item.userId ?? item.UserId;
    if (!userId) return;

    distributeTaskSessionToMap(
      map,
      userId,
      item.startTime ?? item.StartTime,
      item.stopTime ?? item.StopTime,
      referenceNowMs,
      {
        loginLogs,
        clipToLogin: Boolean(loginLogs?.length),
      }
    );
  });
  return map;
}

/** Completed call/meeting sessions only (StopTime set). */
export function buildClosedCallMeetingTaskTimeMap(records, referenceNow = Date.now()) {
  const map = new Map();
  const referenceNowMs = getIstNowMs(referenceNow);

  (records || []).forEach((item) => {
    if (!isCallMeetingTaskItem(item)) return;

    const stopRaw = item.stopTime ?? item.StopTime;
    if (!hasStopTime(stopRaw)) return;

    const userId = item.userid ?? item.Userid ?? item.userId ?? item.UserId;
    if (!userId) return;

    distributeTaskSessionToMap(
      map,
      userId,
      item.startTime ?? item.StartTime,
      stopRaw,
      referenceNowMs
    );
  });
  return map;
}

/** True when a call/meeting task is started but not yet stopped for this user/day. */
export function hasActiveCallMeetingSession(
  records,
  userId,
  workDate,
  referenceNow = Date.now()
) {
  if (!userId || !workDate) return false;

  const referenceNowMs = getIstNowMs(referenceNow);

  return (records || []).some((item) => {
    if (!isCallMeetingTaskItem(item)) return false;

    const uid = item.userid ?? item.Userid ?? item.userId ?? item.UserId;
    if (String(uid) !== String(userId)) return false;

    const stopRaw = item.stopTime ?? item.StopTime;
    if (hasStopTime(stopRaw)) return false;

    const startMs = parseApiDateTimeMs(item.startTime ?? item.StartTime);
    if (Number.isNaN(startMs)) return false;

    const dayStart = startOfIstDayMs(workDate);
    const dayEnd = endOfIstDayMs(dayStart);
    if (Number.isNaN(dayStart) || Number.isNaN(dayEnd)) return false;

    return startMs < dayEnd && referenceNowMs > dayStart;
  });
}

/** Open (running) call/meeting task sessions only — grows until Stop. */
export function buildActiveCallMeetingTaskTimeMap(records, referenceNow = Date.now()) {
  const map = new Map();
  const referenceNowMs = getIstNowMs(referenceNow);

  (records || []).forEach((item) => {
    if (!isCallMeetingTaskItem(item)) return;

    const stopRaw = item.stopTime ?? item.StopTime;
    if (hasStopTime(stopRaw)) return;

    const userId = item.userid ?? item.Userid ?? item.userId ?? item.UserId;
    if (!userId) return;

    distributeTaskSessionToMap(
      map,
      userId,
      item.startTime ?? item.StartTime,
      stopRaw,
      referenceNowMs
    );
  });
  return map;
}

/** Call/meeting tasks — productive only while StartStop session is active (task started). */
export function buildCallMeetingTaskTimeMap(records, referenceNow = Date.now()) {
  const map = new Map();
  const referenceNowMs = getIstNowMs(referenceNow);

  (records || []).forEach((item) => {
    if (!isCallMeetingTaskItem(item)) return;

    const userId = item.userid ?? item.Userid ?? item.userId ?? item.UserId;
    if (!userId) return;

    distributeTaskSessionToMap(
      map,
      userId,
      item.startTime ?? item.StartTime,
      item.stopTime ?? item.StopTime,
      referenceNowMs
    );
  });
  return map;
}

/** Snap click productive only — from API ClickProductive (never merged Productive). */
export function resolveClickProductiveMs(row) {
  const clickRaw = row.clickProductive ?? row.ClickProductive;
  if (clickRaw != null && clickRaw !== '') {
    return hmsToMs(clickRaw);
  }
  return 0;
}

export function sumTaskMinutesForUser(
  records,
  userId,
  startDate,
  endDate,
  referenceNow = Date.now(),
  loginLogs = null
) {
  if (!userId || !startDate || !endDate) return 0;

  const referenceNowMs = getIstNowMs(referenceNow);
  let totalMs = sumTaskMsForUserInRange(
    records,
    userId,
    startDate,
    endDate,
    referenceNowMs,
    loginLogs
  );

  if (startDate === endDate) {
    if (loginLogs?.length) {
      const loginMap = buildLoginTimeMap(loginLogs, referenceNow);
      const loginMs = loginMap.get(`${userId}|${startDate}`) ?? MAX_IST_DAY_MS;
      totalMs = Math.min(totalMs, capMsToDailyLimit(loginMs));
    }
    totalMs = capMsToDailyLimit(totalMs);
  }

  if (totalMs <= 0) return 0;

  let mins = Math.floor(totalMs / 60000);
  if (mins === 0 && totalMs > 0) mins = 1;
  return mins;
}

export function buildUserTaskSummaryList(
  records,
  { startDate, endDate, taskName = '', employeeName = '' },
  referenceNow = Date.now()
) {
  const byKey = new Map();

  (records || []).forEach((item) => {
    if (!matchesTaskFilters(item, {
      userName: employeeName || null,
      taskName,
      startDate,
      endDate,
    }, referenceNow)) return;

    const username = (item.userName || item.UserName || '').trim();
    const label = getTaskLabelFromItem(item) || 'Unknown Task';
    if (!username) return;

    const key = `${username}|${label}`;
    const start = item.startTime ?? item.StartTime;
    const mins = taskDurationMinutesInRange(item, startDate, endDate, referenceNow);

    const existing = byKey.get(key) || {
      userName: username,
      taskName: label,
      totalMinutes: 0,
      maxDate: null,
    };

    existing.totalMinutes += mins;
    if (start) {
      const startMs = parseApiDateTimeMs(start);
      if (!Number.isNaN(startMs)) {
        const maxMs = existing.maxDate
          ? parseApiDateTimeMs(existing.maxDate)
          : Number.NEGATIVE_INFINITY;
        if (startMs > maxMs) {
          existing.maxDate = start;
        }
      }
    }

    byKey.set(key, existing);
  });

  return [...byKey.values()].sort((a, b) => {
    const byUser = a.userName.localeCompare(b.userName);
    if (byUser !== 0) return byUser;
    return a.taskName.localeCompare(b.taskName);
  });
}

/**
 * Dashboard Task Time — exact sum of Task Reports "Total Time" rows for this user/date range.
 */
export function sumUserTaskMinutesForDisplay(
  records,
  userId,
  startDate,
  endDate,
  referenceNow = Date.now()
) {
  if (!userId || !startDate || !endDate) return 0;

  const match = (records || []).find((item) =>
    Number(item.userid ?? item.Userid ?? item.userId ?? item.UserId) === Number(userId)
  );
  const employeeName = (match?.userName ?? match?.UserName ?? '').trim();
  if (!employeeName) return 0;

  return buildUserTaskSummaryList(
    records,
    { startDate, endDate, employeeName },
    referenceNow
  ).reduce((sum, row) => sum + (Number(row.totalMinutes) || 0), 0);
}

export function groupTaskSessionsForUser(
  records,
  userName,
  startDate,
  endDate,
  taskName = '',
  referenceNow = Date.now()
) {
  const groups = new Map();

  (records || []).forEach((item) => {
    if (!matchesTaskFilters(item, { userName, taskName, startDate, endDate }, referenceNow)) return;

    const label = getTaskLabelFromItem(item) || 'Unknown Task';
    const start = item.startTime ?? item.StartTime;
    const stop = item.stopTime ?? item.StopTime;
    const id = item.id ?? item.Id ?? `${start}-${stop}`;

    if (!groups.has(label)) {
      groups.set(label, { taskName: label, sessions: [], totalMinutes: 0 });
    }

    const group = groups.get(label);
    group.sessions.push({ id, start, stop });
  });

  return [...groups.values()]
    .map((group) => ({
      ...group,
      sessions: group.sessions.sort(
        (a, b) => parseApiDateTimeMs(b.start) - parseApiDateTimeMs(a.start)
      ),
    }))
    .sort((a, b) => a.taskName.localeCompare(b.taskName));
}

/** Earliest StartStop start time per travelling user/day (fixed start time on dashboard). */
export function buildFirstTaskStartMap(records, travellingUserIds) {
  const map = new Map();
  const travelling = travellingUserIds instanceof Set
    ? travellingUserIds
    : new Set(travellingUserIds || []);

  (records || []).forEach((item) => {
    const userId = Number(item.userid ?? item.Userid ?? item.userId ?? item.UserId);
    if (!travelling.has(userId)) return;

    const startRaw = item.startTime ?? item.StartTime;
    if (!startRaw) return;

    const startMs = parseApiDateTimeMs(startRaw);
    const dateStr = Number.isNaN(startMs) ? '' : toIstDateString(startMs);
    if (!dateStr) return;

    const key = `${userId}|${dateStr}`;
    if (Number.isNaN(startMs)) return;

    const existing = map.get(key);
    if (!existing || startMs < parseApiDateTimeMs(existing)) {
      map.set(key, startRaw);
    }
  });

  return map;
}
